import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 md:px-10 py-12" data-testid="contact-page">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <p className="text-xs uppercase tracking-[0.25em] text-[hsl(10_51%_55%)]">Concierge</p>
          <h1 className="font-serif-display text-4xl mt-3 leading-tight">Say hello. We reply personally.</h1>
          <p className="mt-5 text-[hsl(100_3%_30%)] leading-relaxed">
            Whether you need help finding the right stay, planning a longer trip, or just have a question — we&apos;re a small team and we&apos;d love to hear from you.
          </p>
          <div className="mt-10 space-y-4 text-sm">
            <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-[hsl(10_51%_55%)]" /> newdaystay@gmail.com</div>
            <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-[hsl(10_51%_55%)]" /> +91 63607 34483</div>
            <div className="flex items-center gap-3"><MapPin className="h-4 w-4 text-[hsl(10_51%_55%)]" /> Assagao, Goa · Indiranagar, Bangalore</div>
            <div className="flex items-center gap-3"><MessageCircle className="h-4 w-4 text-[hsl(10_51%_55%)]" /> WhatsApp: +91 63607 34483</div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="lg:col-span-3 border border-[hsl(165_37%_17%/0.1)] rounded-md p-8 bg-white space-y-5" data-testid="inquiry-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-name">Name</Label>
              <Input id="c-name" value={form.name} onChange={set("name")} required data-testid="contact-name" className="mt-2" />
            </div>
            <div>
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" type="email" value={form.email} onChange={set("email")} required data-testid="contact-email" className="mt-2" />
            </div>
          </div>
          <div>
            <Label htmlFor="c-phone">Phone (optional)</Label>
            <Input id="c-phone" value={form.phone} onChange={set("phone")} data-testid="contact-phone" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="c-subject">Subject</Label>
            <Input id="c-subject" value={form.subject} onChange={set("subject")} required data-testid="contact-subject" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="c-message">Message</Label>
            <Textarea id="c-message" rows={5} value={form.message} onChange={set("message")} required data-testid="contact-message" className="mt-2" />
          </div>
          <Button type="submit" disabled={sending} className="btn-terracotta rounded-full px-8 h-11" data-testid="contact-submit">
            {sending ? "Sending…" : "Send message"}
          </Button>
        </form>
      </div>
    </div>
  );
}
